import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import autoBind from 'react-autobind';
import { Card, CardHeader, CardText } from 'material-ui/Card';
import Divider from 'material-ui/Divider';
import { Marker } from 'react-google-maps';
import { List, ListItem } from 'material-ui/List';
import { PanelContainer } from '../../components/Panel';
import FieldToggle from '../../components/FieldToggle';
import { appContainer, Subscribe } from '../../contexts/app';
import SidebarMap from '../../components/Sidebar/SidebarMap';
import SidebarPage from '../../components/Sidebar/components/SidebarPage';
import Map from '../../components/Map';
import MapOverlay from '../../components/MapOverlay';
import MapMarker from '../../components/MapMarker';
import TrialExpireModal from '../../components/TrialExpireModal';
import { UserAccountContext } from '../../contexts/userAccount';
import noty from '../../lib/noty';
import style from './style.js';
import './style.scss';

class IndexMap extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      trialWarningOpen: this.props.trialWarningOpen,
      goToAcctSettings: false,
      panelOpen: true,
      markerFilter: {}
    };
  }

  async componentDidMount() {
    // Ensure all relative resources are fresh
    // This is a bit redundant on the first app load.
    // Because this page is the index, everthing redirects
    // here after login, and app state is already totally
    // filled from app prep, and this calls again...
    // But it is valuable when navigating to this page
    // later on
    // Update the marker filter with the latest
    // from state, then await as all fresh resources
    // come in and update filter to reflect fresh
    const { facility, isNewSignUp } = this.props;
    if (isNewSignUp) {
      appContainer.set('trainingModalOpen', true);
    }
    this.createMarkerFilter(facility);
    await facility.load();
    this.createMarkerFilter(facility);
  }

  async componentWillReceiveProps(nextProps) {
    const { facility } = this.props;
    const { facility: nextFacility } = nextProps;
    if (facility._id !== nextFacility._id) {
      this.createMarkerFilter(nextFacility);
      await nextFacility.load();
      this.createMarkerFilter(nextFacility);
    }
  }

  handlePanelToggle() {
    this.setState({ panelOpen: !this.state.panelOpen });
  }

  handleCloseTrialModal() {
    this.setState({ trialWarningOpen: false });
    appContainer.set('trialWarningOpen', false);
  }

  handleGoToAccountSettings() {
    appContainer.set('trialWarningOpen', false);
    this.setState({ trialWarningOpen: false, goToAcctSettings: true });
  }

  createMarkerFilter(facility) {
    const buildings = facility.buildings();
    const allFloors = buildings.reduce((allFloors, building) => {
      return [...allFloors, ...building.floors()];
    }, []);
    const markerFilter = allFloors.reduce((markerFilter, floor) => {
      markerFilter[floor._id] = [];
      return markerFilter;
    }, {});
    this.setState({ markerFilter });
  }

  toggleMarkerFilter(floorId, markerTypeId) {
    const types = this.state.markerFilter[floorId];
    const newTypes =
      types.indexOf(markerTypeId) >= 0
        ? types.filter(type => type !== markerTypeId)
        : [...types, markerTypeId];
    const markerFilter = Object.assign({}, this.state.markerFilter, {
      [floorId]: newTypes
    });
    this.setState({ markerFilter });
  }

  toggleAllMarkerFilter(floorId, markerTypes) {
    const markerFilter = {
      ...this.state.markerFilter,
      [floorId]: markerTypes
    };
    this.setState({ markerFilter });
  }

  render() {
    const { buildingId } = this.props.match.params;
    const {
      facility,
      buildings,
      floors,
      currentUser,
      subscriptionStatus,
      permissions,
      ownerName
    } = this.props;
    const facilityCenter = facility.geo.coordinates;
    const center = buildingId
      ? buildings.get(buildingId).geo.coordinates
      : facilityCenter;
    const { markerFilter, trialWarningOpen, goToAcctSettings } = this.state;

    let {
      trialDaysRemaining,
      trialWarning,
      trialExpiration
    } = subscriptionStatus;
    var accountEditPermission = permissions.toList().filter(permission => {
      return (
        permission.user_id === currentUser.get('_id') &&
        permission.permissions.edit.account
      );
    });
    if (goToAcctSettings) {
      return <Redirect to="account/settings" />;
    }
    return (
      <div id="mapPage" className="authed-page">
        {trialWarning && trialWarningOpen ? (
          <TrialExpireModal
            ownerName={ownerName}
            trialDaysRemaining={trialDaysRemaining}
            trialExpiration={trialExpiration}
            accountEditPermission={accountEditPermission}
            onRequestClose={this.handleCloseTrialModal}
            onAccountClick={this.handleGoToAccountSettings}
          />
        ) : null}
        <SidebarMap facility={facility} match={this.props.match} />
        <SidebarPage>
          <PanelContainer
            width={475}
            open={this.state.panelOpen}
            onToggle={this.handlePanelToggle}
          >
            <Switch>
              <Route
                exact
                path="/map/:facilityId"
                render={routeProps => <FacilityPanel facility={facility} />}
              />
              <Route
                exact
                path="/map/:facilityId/buildings/:buildingId"
                render={routeProps => (
                  <BuildingPanel building={buildings.get(buildingId)} />
                )}
              />
              <Route
                exact
                path="/map/:facilityId/buildings/:buildingId/floors/:floorId"
                render={routeProps => {
                  const { floorId } = routeProps.match.params;
                  return (
                    <FloorPanel
                      floor={floors.get(floorId)}
                      toggleMarkerFilter={this.toggleMarkerFilter}
                      toggleAllMarkerFilter={this.toggleAllMarkerFilter}
                      markerFilter={markerFilter}
                    />
                  );
                }}
              />
            </Switch>
          </PanelContainer>
          <Map center={center}>
            <Switch>
              <Route
                exact
                path="/map/:facilityId/buildings/:buildingId"
                render={props => {
                  const { buildingId } = this.props.match.params;
                  const building = buildings.get(buildingId);
                  return (
                    <Marker
                      position={{
                        lat: building.geo.coordinates[1],
                        lng: building.geo.coordinates[0]
                      }}
                    />
                  );
                }}
              />
              <Route
                exact
                path="/map/:facilityId/buildings/:buildingId/floors/:floorId"
                render={props => {
                  const { floorId } = this.props.match.params;
                  const floor = floors.get(floorId);
                  return (
                    <div>
                      <MapOverlay map={window.MP_MAP_REF} floor={floor} />
                      {floor.markers().map((marker, index) => {
                        const floorFilter = markerFilter[floor._id];
                        if (floorFilter.indexOf(marker.type_id) >= 0) {
                          return (
                            <MapMarker
                              marker={marker}
                              floor={floor}
                              key={index}
                            />
                          );
                        } else {
                          return null;
                        }
                      })}
                    </div>
                  );
                }}
              />
            </Switch>
          </Map>
        </SidebarPage>
      </div>
    );
  }
}

export default routeProps => {
  return (
    <UserAccountContext.Consumer>
      {({ subscriptionStatus }) => {
        return (
          <Subscribe
            to={[appContainer]}
            render={app => {
              const isNewSignUp = routeProps.location.search === '?signup=true';
              const { facilityId } = routeProps.match.params;
              if (!facilityId) {
                // /map is the index page, just redirect
                const firstFacility = app.state.get('facilities').first();
                if (firstFacility) {
                  return <Redirect to={`/map/${firstFacility._id}`} />;
                } else {
                  noty.errors(
                    'It appears you do not have access to any facilities. Contact your administrator to be given access.'
                  );
                  return <Redirect to="/account" />;
                }
              }
              return (
                <IndexMap
                  {...routeProps}
                  isNewSignUp={isNewSignUp}
                  facility={app.state.getIn(['facilities', facilityId])}
                  facilities={app.state.get('facilities')}
                  buildings={app.state.get('buildings')}
                  floors={app.state.get('floors')}
                  markers={app.state.get('markers')}
                  infographics={app.state.get('infographics')}
                  currentUser={app.state.get('currentUser')}
                  trialWarningOpen={app.state.get('trialWarningOpen')}
                  ownerName={app.state.get('ownerName')}
                  permissions={app.state.get('permissions')}
                  subscriptionStatus={subscriptionStatus}
                />
              );
            }}
          />
        );
      }}
    </UserAccountContext.Consumer>
  );
};

const FacilityPanel = ({ facility }) => (
  <Card>
    <CardHeader
      style={style.cardHeader}
      title={`${facility.name}, ${facility.short_name}`}
      subtitle={facility.address}
    />
  </Card>
);

const BuildingPanel = ({ building }) => (
  <Card>
    <CardHeader
      style={style.cardHeader}
      title={building.name}
      subtitle={building.address}
    />
  </Card>
);

const FloorPanel = ({
  floor,
  toggleMarkerFilter,
  toggleAllMarkerFilter,
  markerFilter
}) => {
  const markers = floor.markers();
  const markerTypes = getMarkerTypes(floor.markers());
  const allToggled =
    markerFilter[floor._id] &&
    markerFilter[floor._id].length === markerTypes.length;
  return (
    <Card>
      <CardHeader
        style={style.cardHeader}
        title={floor.name}
        subtitle={`${markers.size} markers`}
      />
      {markers.size && (
        <CardText style={{ paddingTop: 0 }}>
          <List style={style.markerList}>
            {markerTypes.map(markerType => {
              const floorFilter = markerFilter[floor._id];
              const isSelected =
                floorFilter && floorFilter.indexOf(markerType._id) >= 0;
              return (
                <div key={markerType._id}>
                  <ListItem
                    primaryText={markerType.name}
                    leftIcon={
                      <img src={markerType.image.path} alt={markerType.name} />
                    }
                    rightToggle={
                      <FieldToggle
                        value={isSelected}
                        onChange={() =>
                          toggleMarkerFilter(floor._id, markerType._id)
                        }
                      />
                    }
                  />
                  <Divider />
                </div>
              );
            })}
          </List>
          <div className="row end-xs" style={style.toggleContainer}>
            <div className="col-xs-6">
              <FieldToggle
                value={allToggled}
                label="Toggle All"
                labelPosition="left"
                onChange={() => {
                  const typeIds = allToggled
                    ? []
                    : markerTypes.map(type => type._id);
                  toggleAllMarkerFilter(floor._id, typeIds);
                }}
              />
            </div>
          </div>
        </CardText>
      )}
    </Card>
  );
};

// Create an array of all marker types on a floor
const getMarkerTypes = markers => {
  return markers.reduce((types, marker, index) => {
    const inArray = types.find(type => type._id === marker.type_id);
    return inArray ? types : [...types, marker.type()];
  }, []);
};



// WEBPACK FOOTER //
// ./src/pages/Map/index.jsx